import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ICondoBookingSummary,
  ISessionKey,
  ICondoGuestBooking,
  IErrorField,
  ICondoBookPackageWithCard,
  ICondoBookingComplete,
  ICountriesCode,
  BookingErrorsEnum,
  ICondoCardBooking,
} from 'src/common-types/index';
import { AppThunk } from 'src/store';
import { axiosInstance, getHeaders, UrlUtils } from 'src/utils/index';
import {
  ERROR,
  EXTERNAL_ERROR,
  PRICE_CHANGED,
  SESSION_EXPIRED_STATUS,
  SESSION_SEARCH_EXPIRED_MESSAGE,
  STATUS_SUCCEEDED,
  Urls,
  VALIDATION_ERROR,
  STATUS_FAILED,
  STATUS_PENDING,
  CONDO_BOOKING_GUEST_INFO_LABEL,
  CONDO_BOOKING_CARD_INFO_LABEL,
  CONDO_BOOKING_SAME_ADDRESS_INFO_LABEL,
  CONDO_UNITS_SESSION_KEY_LABEL,
  CONDO_SESSION_KEY,
  CONDO_UNITS_SEARCH_LABEL,
  SAVING_MISMATCH,
} from 'src/constants/index';

export interface ICondoReviewBookState {
  condoBookingSummary: ICondoBookingSummary;
  loading: boolean;
  error: string;
  isUpdatePrice: boolean;
  guest: ICondoGuestBooking;
  card: ICondoCardBooking;
  errorsField: IErrorField[];
  loadingBooking: boolean;
  bookingComplete: boolean;
  isBookingInProgress: boolean;
  booking: ICondoBookingComplete;
  errorBooking: string;
  isExternalError: boolean;
  isPriceChangedError: boolean;
  bookingErrorCode: BookingErrorsEnum;
  errorsBookingMessage: IErrorField[];
  condosCountriesCode: ICountriesCode;
  errorCountries: string;
  expiredSession: boolean;
  isBookingPending: boolean;
  isAlternativeUnitsAvailable: boolean;
  isErrorMessageExpirationDate: boolean;
  isErrorMessageCardNumber: boolean;
  isErrorMessageZipCode: boolean;
  isSavingsMismatch: boolean;
}

const initialState: ICondoReviewBookState = {
  condoBookingSummary: null,
  loading: false,
  error: '',
  isUpdatePrice: false,
  guest: null,
  errorsField: null,
  card: null,
  loadingBooking: false,
  bookingComplete: false,
  isBookingInProgress: false,
  booking: null,
  errorBooking: '',
  isExternalError: false,
  isPriceChangedError: false,
  errorsBookingMessage: null,
  condosCountriesCode: null,
  errorCountries: '',
  expiredSession: false,
  bookingErrorCode: null,
  isBookingPending: false,
  isAlternativeUnitsAvailable: false,
  isErrorMessageExpirationDate: true,
  isErrorMessageCardNumber: true,
  isErrorMessageZipCode: true,
  isSavingsMismatch: false,
};

const zeroItem = 0;

const condoReviewBookSlice = createSlice({
  name: 'condo-review-book',
  initialState,
  reducers: {
    setCondoReviewBook: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<ICondoBookingSummary>,
    ) => {
      state.condoBookingSummary = payload;
    },
    setLoadingCondoBook: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setIsBookingPending: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isBookingPending = payload;
    },
    setIsAlternativeUnitsAvailable: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isAlternativeUnitsAvailable = payload;
    },
    setErrorCondoBook: (state: ICondoReviewBookState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setUpdateCondoPriceReview: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isUpdatePrice = payload;
    },
    setCondoGuest: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<ICondoGuestBooking>,
    ) => {
      state.guest = payload;
    },
    setCondoCard: (state: ICondoReviewBookState, { payload }: PayloadAction<ICondoCardBooking>) => {
      state.card = payload;
    },
    setLoadingCondoBooking: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.loadingBooking = payload;
    },
    setCondoBookingComplete: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.bookingComplete = payload;
    },
    setCondoBookingInProgress: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isBookingInProgress = payload;
    },
    setCondoBooking: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<ICondoBookingComplete>,
    ) => {
      state.booking = payload;
    },
    setCondoErrorBooking: (state: ICondoReviewBookState, { payload }: PayloadAction<string>) => {
      state.errorBooking = payload;
    },
    setErrorsCondoField: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<IErrorField[]>,
    ) => {
      state.errorsField = payload;
    },
    setCondoExternalError: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isExternalError = payload;
    },
    setCondoSavingsMismatch: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isSavingsMismatch = payload;
    },
    setCondoPriceChangedError: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isPriceChangedError = payload;
    },
    setBookingErrorCodeError: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<BookingErrorsEnum>,
    ) => {
      state.bookingErrorCode = payload;
    },
    setCondoErrorsBookingMessage: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<IErrorField[]>,
    ) => {
      state.errorsBookingMessage = payload;
    },
    setCondosCountriesCode: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<ICountriesCode>,
    ) => {
      state.condosCountriesCode = payload;
    },
    setErrorCountries: (state: ICondoReviewBookState, { payload }: PayloadAction<string>) => {
      state.errorCountries = payload;
    },
    setCondoExpiredSession: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.expiredSession = payload;
    },
    condoBookingResetState: () => {
      return initialState;
    },
    setErrorMessageExpirationDate: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isErrorMessageExpirationDate = payload;
    },
    setErrorMessageCardNumber: (
      state: ICondoReviewBookState,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isErrorMessageCardNumber = payload;
    },
    setErrorMessageZipCode: (state: ICondoReviewBookState, { payload }: PayloadAction<boolean>) => {
      state.isErrorMessageZipCode = payload;
    },
  },
});

export const {
  setCondoReviewBook,
  setLoadingCondoBook,
  setErrorCondoBook,
  setUpdateCondoPriceReview,
  setCondoGuest,
  setCondoCard,
  setLoadingCondoBooking,
  setCondoBookingComplete,
  setCondoBookingInProgress,
  setCondoBooking,
  setCondoErrorBooking,
  setErrorsCondoField,
  setCondoExternalError,
  setCondoPriceChangedError,
  setCondoErrorsBookingMessage,
  setCondosCountriesCode,
  setErrorCountries,
  setCondoExpiredSession,
  setBookingErrorCodeError,
  setIsBookingPending,
  setIsAlternativeUnitsAvailable,
  condoBookingResetState,
  setErrorMessageExpirationDate,
  setErrorMessageCardNumber,
  setErrorMessageZipCode,
  setCondoSavingsMismatch,
} = condoReviewBookSlice.actions;

export const condoReviewBookReducer = condoReviewBookSlice.reducer;

export const getCountries = (
  condoId: number,
  availabilityId: string,
  sessionKey: ISessionKey,
): AppThunk => {
  return async (dispatch) => {
    try {
      const res = await axiosInstance.post(
        Urls.CondoCountries,
        { condoId, availabilityId, sessionKey },
        { ...getHeaders() },
      );
      dispatch(setCondosCountriesCode(res.data));
    } catch (error) {
      dispatch(setErrorCountries(error.toString()));
    }
  };
};

export const getCondoReviewBook = (
  condoId: number,
  availabilityId: string,
  sessionKey: ISessionKey,
  isGetCoupon?: false,
): AppThunk => {
  return async (dispatch, getState) => {
    const { condoRedeemCodeStore } = getState();

    if (!(condoRedeemCodeStore.isGetCoupon || isGetCoupon)) {
      dispatch(setLoadingCondoBook(true));
    }
    dispatch(setUpdateCondoPriceReview(true));
    dispatch(setCondoExpiredSession(false));

    try {
      const res = await axiosInstance.post(
        Urls.CondoBookingSummary,
        { condoId, availabilityId, sessionKey },
        { ...getHeaders() },
      );

      dispatch(setUpdateCondoPriceReview(false));
      dispatch(setLoadingCondoBook(false));
      dispatch(setCondoReviewBook(res.data));
    } catch (error) {
      dispatch(setErrorCondoBook(error.toString()));
      dispatch(setLoadingCondoBook(false));
      dispatch(setUpdateCondoPriceReview(false));

      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data[zeroItem] === SESSION_SEARCH_EXPIRED_MESSAGE
      ) {
        dispatch(setCondoExpiredSession(true));
      }
    }
  };
};

export const getCondoBookPackageWithCard = (bookWithCard: ICondoBookPackageWithCard): AppThunk => {
  return async (dispatch, getState) => {
    dispatch(setLoadingCondoBooking(true));
    dispatch(setBookingErrorCodeError(null));
    dispatch(setIsBookingPending(false));
    dispatch(setIsAlternativeUnitsAvailable(false));
    dispatch(setCondoExpiredSession(false));

    const postData: ICondoBookPackageWithCard = {
      ...bookWithCard,
      guests: bookWithCard.guests.map((guest: ICondoGuestBooking) => {
        return {
          ...guest,
          givenName: guest.givenName.trim(),
          surname: guest.surname.trim(),
        };
      }),
    };

    try {
      const res = await axiosInstance.post(Urls.CondoBook, postData, {
        ...getHeaders(),
      });
      dispatch(setLoadingCondoBooking(false));

      if (res.data.status === STATUS_SUCCEEDED) {
        dispatch(setCondoBookingComplete(true));
        localStorage.setItem(CONDO_BOOKING_GUEST_INFO_LABEL, '');
        localStorage.setItem(CONDO_BOOKING_CARD_INFO_LABEL, '');
        localStorage.setItem(CONDO_BOOKING_SAME_ADDRESS_INFO_LABEL, '');

        const { navigationMenuStore } = getState();

        if (navigationMenuStore?.items?.promo) {
          UrlUtils.removeFromUrl(CONDO_UNITS_SESSION_KEY_LABEL);
          UrlUtils.removeFromUrl(CONDO_SESSION_KEY);
          UrlUtils.removeFromUrl(CONDO_UNITS_SEARCH_LABEL);
        }
      } else if (res.data.status === STATUS_FAILED) {
        if (res.data.error?.errorCode) {
          const { errorCode } = res.data.error;

          dispatch(setBookingErrorCodeError(errorCode));
        } else {
          dispatch(setBookingErrorCodeError(BookingErrorsEnum.UnexpectedResponse));
        }

        dispatch(setIsAlternativeUnitsAvailable(!!res.data?.isAlternativeUnitsAvailable));
      } else if (res.data.status === STATUS_PENDING) {
        dispatch(setIsBookingPending(true));
      } else {
        dispatch(setCondoBookingInProgress(true));
      }

      dispatch(setCondoBooking(res.data));
    } catch (error) {
      dispatch(setCondoErrorBooking(error.toString()));
      dispatch(setLoadingCondoBooking(false));

      if (error?.response?.data && error?.response?.data[zeroItem].errorType === VALIDATION_ERROR) {
        dispatch(setErrorsCondoField(error.response.data));
      } else if (
        error?.response?.data &&
        error?.response?.data[zeroItem].errorType === EXTERNAL_ERROR
      ) {
        dispatch(setCondoExternalError(true));
      } else if (
        error?.response?.data &&
        error?.response?.data[zeroItem].errorCode === PRICE_CHANGED
      ) {
        dispatch(setCondoPriceChangedError(true));
      } else if (error?.response?.data && error?.response?.data[zeroItem].errorType === ERROR) {
        dispatch(setCondoErrorsBookingMessage(error.response.data));
      } else if (error?.response?.data[zeroItem].errorCode === SAVING_MISMATCH) {
        dispatch(setCondoExpiredSession(true));
        dispatch(setCondoSavingsMismatch(true));
      }
    }
  };
};
